<template>
  <v-layout align-start>
    <v-flex>
      <v-tabs v-model="activeTab" color="deep-purple">
        <v-tab>
          <v-icon left small>school</v-icon>
          Talleres
        </v-tab>
        <v-tab>
          <v-icon left small>how_to_reg</v-icon>
          Solicitudes
          <v-chip x-small color="deep-purple" dark class="ml-2" v-if="pendingCount">{{ pendingCount }}</v-chip>
        </v-tab>
        <v-tab>
          <v-icon left small>list_alt</v-icon>
          Todas las Inscripciones
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">

        <!-- ===== TAB 1: TALLERES ===== -->
        <v-tab-item>
          <v-toolbar flat class="mt-2">
            <v-toolbar-title>Talleres de Embajadores</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn color="deep-purple" @click="openCreateDialog()">
              <v-icon left>add</v-icon>Nuevo Taller
            </v-btn>
          </v-toolbar>

          <v-data-table
            no-data-text="No hay talleres creados"
            loading-text="Cargando…" :headers="workshopHeaders" :items="workshops" class="elevation-1" :loading="loading">
            <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
            <template v-slot:item.status="{ item }">
              <v-chip small :color="statusColor(item.status)" dark>{{ item.status }}</v-chip>
            </template>
            <template v-slot:item.enrolledCount="{ item }">
              {{ item.enrolledCount }}{{ item.maxCapacity ? ' / ' + item.maxCapacity : '' }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon small class="mr-2" @click="openEditDialog(item)" title="Editar">edit</v-icon>
              <v-icon small class="mr-2" @click="viewEnrollments(item)" title="Ver inscriptos">people</v-icon>
              <v-icon small color="red" @click="confirmDelete(item)" title="Eliminar">delete</v-icon>
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- ===== TAB 2: SOLICITUDES ===== -->
        <v-tab-item>
          <v-toolbar flat class="mt-2">
            <v-toolbar-title>Solicitudes para ser Embajador</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-text-field v-model="searchSolicitudes" append-icon="search" label="Buscar" single-line hide-details dense outlined style="max-width:280px" class="mr-3"></v-text-field>
            <v-btn icon @click="loadEnrollments" title="Actualizar"><v-icon>refresh</v-icon></v-btn>
          </v-toolbar>

          <v-alert type="info" outlined dense class="ma-4" style="font-size:0.85rem">
            Afiliados que registraron interés en ser embajadores. Podés cambiar su estado o promoverlos directamente al rol de Embajador.
          </v-alert>

          <v-data-table
            no-data-text="No hay solicitudes pendientes"
            loading-text="Cargando…"
            :headers="solicitudesHeaders"
            :items="solicitudes"
            :search="searchSolicitudes"
            :loading="loadingEnrollments"
            class="elevation-1"
            sort-by="enrolledAt"
            sort-desc
          >
            <template v-slot:item.enrolledAt="{ item }">{{ formatDate(item.enrolledAt) }}</template>

            <template v-slot:item.status="{ item }">
              <v-chip small :color="enrollStatusColor(item.status)" dark>{{ item.status }}</v-chip>
            </template>

            <template v-slot:item.isAmbassador="{ item }">
              <v-chip v-if="item.isAmbassador" small color="green" dark>
                <v-icon x-small left>check_circle</v-icon>Embajador
              </v-chip>
              <span v-else class="grey--text text--darken-1 caption">—</span>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="d-flex align-center gap-2">
                <v-select
                  v-model="item.status"
                  :items="enrollStatuses"
                  dense
                  hide-details
                  outlined
                  style="max-width:150px; font-size:0.8rem"
                  @change="updateEnrollmentStatus(item)"
                ></v-select>
                <v-btn
                  v-if="!item.isAmbassador"
                  small
                  color="deep-purple"
                  dark
                  class="ml-2"
                  :loading="promoting === item._id"
                  @click="promoteToAmbassador(item)"
                  title="Asignar rol Embajador"
                >
                  <v-icon small left>military_tech</v-icon>
                  Promover
                </v-btn>
                <v-chip v-else small color="green" outlined class="ml-2">
                  <v-icon small left>check</v-icon>Ya es Embajador
                </v-chip>
              </div>
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- ===== TAB 3: TODAS LAS INSCRIPCIONES ===== -->
        <v-tab-item>
          <v-toolbar flat class="mt-2">
            <v-toolbar-title>Todas las inscripciones a talleres</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-text-field v-model="searchEnrollments" append-icon="search" label="Buscar" single-line hide-details dense outlined style="max-width:280px" class="mr-3"></v-text-field>
          </v-toolbar>

          <v-data-table
            no-data-text="No hay inscripciones registradas"
            loading-text="Cargando…"
            :headers="enrollmentHeaders"
            :items="workshopEnrollmentsList"
            :search="searchEnrollments"
            class="elevation-1"
            :loading="loadingEnrollments"
            sort-by="enrolledAt"
            sort-desc
          >
            <template v-slot:item.enrolledAt="{ item }">{{ formatDate(item.enrolledAt) }}</template>
            <template v-slot:item.status="{ item }">
              <v-chip small :color="enrollStatusColor(item.status)" dark>{{ item.status }}</v-chip>
            </template>
            <template v-slot:item.isAmbassador="{ item }">
              <v-chip v-if="item.isAmbassador" small color="green" dark>
                <v-icon x-small left>check_circle</v-icon>Embajador
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <div class="d-flex align-center">
                <v-select
                  v-model="item.status"
                  :items="enrollStatuses"
                  dense
                  hide-details
                  outlined
                  style="max-width:150px; font-size:0.8rem"
                  @change="updateEnrollmentStatus(item)"
                ></v-select>
                <v-btn
                  v-if="!item.isAmbassador"
                  small
                  color="deep-purple"
                  dark
                  class="ml-2"
                  :loading="promoting === item._id"
                  @click="promoteToAmbassador(item)"
                >
                  <v-icon small left>military_tech</v-icon>
                  Promover
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-tab-item>

      </v-tabs-items>

      <!-- Dialog crear/editar taller -->
      <v-dialog v-model="dialogWorkshop" max-width="600">
        <v-card>
          <v-card-title>{{ editingWorkshop ? 'Editar Taller' : 'Nuevo Taller' }}</v-card-title>
          <v-card-text>
            <v-text-field v-model="form.title" label="Título" outlined></v-text-field>
            <v-textarea v-model="form.description" label="Descripción (opcional)" outlined rows="3"></v-textarea>
            <v-text-field v-model="form.date" label="Fecha y hora" type="datetime-local" outlined></v-text-field>
            <v-text-field v-model.number="form.durationMinutes" label="Duración (minutos)" type="number" outlined></v-text-field>
            <v-text-field v-model="form.zoomLink" label="Link de Zoom" outlined></v-text-field>
            <v-text-field v-model="form.zoomId" label="ID de Zoom (opcional)" outlined></v-text-field>
            <v-text-field v-model="form.zoomPassword" label="Contraseña Zoom (opcional)" outlined></v-text-field>
            <v-text-field v-model.number="form.maxCapacity" label="Capacidad máxima (vacío = sin límite)" type="number" outlined></v-text-field>
            <v-select v-if="editingWorkshop" v-model="form.status" :items="workshopStatuses" label="Estado" outlined></v-select>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="dialogWorkshop = false">Cancelar</v-btn>
            <v-btn color="primary" @click="saveWorkshop" :loading="saving">Guardar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog inscriptos de un taller -->
      <v-dialog v-model="dialogEnrollments" max-width="700">
        <v-card>
          <v-card-title>Inscriptos — {{ selectedWorkshop && selectedWorkshop.title }}</v-card-title>
          <v-card-text>
            <v-data-table
            no-data-text="Este taller todavía no tiene inscriptos"
            loading-text="Cargando…" :headers="workshopEnrollmentHeaders" :items="workshopEnrollmentsDialog" dense class="elevation-0">
              <template v-slot:item.enrolledAt="{ item }">{{ formatDate(item.enrolledAt) }}</template>
              <template v-slot:item.status="{ item }">
                <v-chip small :color="enrollStatusColor(item.status)" dark>{{ item.status }}</v-chip>
              </template>
            </v-data-table>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="dialogEnrollments = false">Cerrar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Confirm delete -->
      <v-dialog v-model="dialogDelete" max-width="400">
        <v-card>
          <v-card-title>Confirmar eliminación</v-card-title>
          <v-card-text>¿Estás seguro de eliminar este taller? Los inscriptos perderán su registro.</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="dialogDelete = false">Cancelar</v-btn>
            <v-btn color="red" dark @click="deleteWorkshop" :loading="deleting">Eliminar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Confirm promote -->
      <v-dialog v-model="dialogPromote" max-width="420">
        <v-card>
          <v-card-title class="deep-purple white--text">
            <v-icon left color="white">military_tech</v-icon>
            Promover a Embajador
          </v-card-title>
          <v-card-text class="pt-4">
            <p>¿Querés asignarle el rol de <strong>Embajador</strong> a:</p>
            <p class="subtitle-1 font-weight-bold">{{ promotingItem && promotingItem.affiliateName }}</p>
            <p class="caption grey--text">{{ promotingItem && promotingItem.affiliateEmail }}</p>
            <p class="body-2 mt-2">El estado de la inscripción pasará a <strong>COMPLETADO</strong> automáticamente.</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="dialogPromote = false">Cancelar</v-btn>
            <v-btn color="deep-purple" dark @click="confirmPromote" :loading="promoting === (promotingItem && promotingItem._id)">
              Confirmar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right :timeout="3500">
        {{ snackbar.text }}
        <template v-slot:action>
          <v-btn text @click="snackbar.show = false">Cerrar</v-btn>
        </template>
      </v-snackbar>

    </v-flex>
  </v-layout>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      activeTab: 0,
      workshops: [],
      allEnrollments: [],
      loading: false,
      loadingEnrollments: false,
      saving: false,
      deleting: false,
      promoting: null,
      dialogWorkshop: false,
      dialogEnrollments: false,
      dialogDelete: false,
      dialogPromote: false,
      editingWorkshop: null,
      selectedWorkshop: null,
      workshopEnrollmentsDialog: [],
      promotingItem: null,
      searchEnrollments: "",
      searchSolicitudes: "",
      form: this.emptyForm(),
      snackbar: { show: false, text: "", color: "success" },
      workshopHeaders: [
        { text: "Título", value: "title" },
        { text: "Fecha", value: "date" },
        { text: "Duración", value: "durationMinutes" },
        { text: "Estado", value: "status" },
        { text: "Inscriptos", value: "enrolledCount" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      solicitudesHeaders: [
        { text: "Nombre", value: "affiliateName" },
        { text: "Email", value: "affiliateEmail" },
        { text: "Solicitud", value: "enrolledAt" },
        { text: "Estado", value: "status" },
        { text: "Rol actual", value: "isAmbassador" },
        { text: "Acciones", value: "actions", sortable: false, width: "320px" },
      ],
      enrollmentHeaders: [
        { text: "Nombre", value: "affiliateName" },
        { text: "Email", value: "affiliateEmail" },
        { text: "Inscripto", value: "enrolledAt" },
        { text: "Estado", value: "status" },
        { text: "Embajador", value: "isAmbassador" },
        { text: "Acciones", value: "actions", sortable: false, width: "300px" },
      ],
      workshopEnrollmentHeaders: [
        { text: "Nombre", value: "affiliateName" },
        { text: "Email", value: "affiliateEmail" },
        { text: "Estado", value: "status" },
        { text: "Fecha", value: "enrolledAt" },
      ],
      workshopStatuses: ["PROGRAMADO", "EN_CURSO", "COMPLETADO", "CANCELADO"],
      enrollStatuses: ["INSCRIPTO", "CONTACTADO", "EN_CURSO", "COMPLETADO", "CANCELADO"],
    };
  },
  computed: {
    // Solicitudes: registros de interés sin taller asignado
    solicitudes() {
      return this.allEnrollments.filter(e => !e.workshopId);
    },
    // Inscripciones a talleres específicos
    workshopEnrollmentsList() {
      return this.allEnrollments.filter(e => !!e.workshopId);
    },
    pendingCount() {
      return this.solicitudes.filter(e => e.status === "INSCRIPTO" && !e.isAmbassador).length;
    },
  },
  async created() {
    await this.loadWorkshops();
    await this.loadEnrollments();
  },
  methods: {
    emptyForm() {
      return {
        title: "Taller de Formación de Embajadores",
        description: "",
        date: "",
        durationMinutes: 180,
        zoomLink: "",
        zoomId: "",
        zoomPassword: "",
        maxCapacity: null,
        status: "PROGRAMADO",
      };
    },
    async loadWorkshops() {
      this.loading = true;
      try {
        const { data } = await axios.get("ambassador-enrollments/workshops");
        this.workshops = data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async loadEnrollments() {
      this.loadingEnrollments = true;
      try {
        const { data } = await axios.get("ambassador-enrollments");
        this.allEnrollments = data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingEnrollments = false;
      }
    },
    openCreateDialog() {
      this.editingWorkshop = null;
      this.form = this.emptyForm();
      this.dialogWorkshop = true;
    },
    openEditDialog(item) {
      this.editingWorkshop = item;
      this.form = {
        title: item.title,
        description: item.description || "",
        date: item.date ? new Date(item.date).toISOString().slice(0, 16) : "",
        durationMinutes: item.durationMinutes,
        zoomLink: item.zoomLink || "",
        zoomId: item.zoomId || "",
        zoomPassword: item.zoomPassword || "",
        maxCapacity: item.maxCapacity,
        status: item.status,
      };
      this.dialogWorkshop = true;
    },
    async saveWorkshop() {
      this.saving = true;
      try {
        if (this.editingWorkshop) {
          await axios.put(`ambassador-enrollments/workshops/${this.editingWorkshop._id}`, this.form);
        } else {
          await axios.post("ambassador-enrollments/workshops", this.form);
        }
        this.dialogWorkshop = false;
        await this.loadWorkshops();
      } catch (e) {
        this.notify((e && e.response && e.response.data && e.response.data.message) || "Error al guardar", "error");
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(item) {
      this.selectedWorkshop = item;
      this.dialogDelete = true;
    },
    async deleteWorkshop() {
      this.deleting = true;
      try {
        await axios.delete(`ambassador-enrollments/workshops/${this.selectedWorkshop._id}`);
        this.dialogDelete = false;
        await this.loadWorkshops();
      } catch (e) {
        this.notify((e && e.response && e.response.data && e.response.data.message) || "Error al eliminar", "error");
      } finally {
        this.deleting = false;
      }
    },
    async viewEnrollments(item) {
      this.selectedWorkshop = item;
      try {
        const { data } = await axios.get(`ambassador-enrollments/workshops/${item._id}/enrollments`);
        this.workshopEnrollmentsDialog = data;
        this.dialogEnrollments = true;
      } catch (e) {
        console.error(e);
      }
    },
    async updateEnrollmentStatus(item) {
      try {
        await axios.patch(`ambassador-enrollments/${item._id}/status`, { status: item.status });
        this.notify("Estado actualizado");
      } catch (e) {
        this.notify("Error al actualizar estado", "error");
      }
    },
    promoteToAmbassador(item) {
      this.promotingItem = item;
      this.dialogPromote = true;
    },
    async confirmPromote() {
      if (!this.promotingItem) return;
      this.promoting = this.promotingItem._id;
      try {
        await axios.post(`ambassador-enrollments/${this.promotingItem._id}/promote`);
        this.promotingItem.isAmbassador = true;
        this.promotingItem.status = "COMPLETADO";
        this.dialogPromote = false;
        this.notify(`${this.promotingItem.affiliateName} es ahora Embajador`, "success");
        this.promotingItem = null;
      } catch (e) {
        this.notify((e && e.response && e.response.data && e.response.data.message) || "Error al promover", "error");
      } finally {
        this.promoting = null;
      }
    },
    notify(text, color = "success") {
      this.snackbar = { show: true, text, color };
    },
    formatDate(d) {
      if (!d) return "";
      return new Date(d).toLocaleString("es-UY", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    },
    statusColor(s) {
      const map = { PROGRAMADO: "blue", EN_CURSO: "orange", COMPLETADO: "green", CANCELADO: "grey" };
      return map[s] || "grey";
    },
    enrollStatusColor(s) {
      const map = { INSCRIPTO: "purple", CONTACTADO: "amber darken-2", EN_CURSO: "blue", COMPLETADO: "green", CANCELADO: "grey" };
      return map[s] || "grey";
    },
  },
};
</script>
